import { useEffect, useRef, useState } from "react";
import { Plus, GripVertical, Pencil, Trash2, Eye, Upload } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import { PublisherCard } from "../../components/landing/Publishers";
import api, { formatApiError, imageUrl } from "../../lib/api";

const EMPTY_FORM = {
  name: "",
  tiktok_username: "",
  category: "",
  description: "",
  tiktok_url: "",
  profile_image: "",
  is_active: true,
  show_on_homepage: true,
};

export default function Publishers() {
  const [pubs, setPubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const fileRef = useRef(null);

  const fetchPubs = () => {
    api.get("/admin/publishers").then((r) => setPubs(r.data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  };

  useEffect(fetchPubs, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (pub) => {
    setEditing(pub);
    setForm({
      name: pub.name || "",
      tiktok_username: pub.tiktok_username || "",
      category: pub.category || "",
      description: pub.description || "",
      tiktok_url: pub.tiktok_url || "",
      profile_image: pub.profile_image || "",
      is_active: pub.is_active,
      show_on_homepage: pub.show_on_homepage,
    });
    setDialogOpen(true);
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, profile_image: res.data.url }));
      toast.success("Görsel yüklendi");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!form.name.trim() || !form.tiktok_username.trim()) {
      toast.error("Yayıncı adı ve TikTok kullanıcı adı zorunludur");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/admin/publishers/${editing.id}`, form);
        toast.success("Yayıncı güncellendi");
      } else {
        await api.post("/admin/publishers", form);
        toast.success("Yayıncı eklendi");
      }
      setDialogOpen(false);
      fetchPubs();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/publishers/${deleteId}`);
      toast.success("Yayıncı silindi");
      setPubs((prev) => prev.filter((p) => p.id !== deleteId));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleteId(null);
    }
  };

  const toggle = async (pub, key) => {
    try {
      await api.patch(`/admin/publishers/${pub.id}`, { [key]: !pub[key] });
      setPubs((prev) => prev.map((p) => (p.id === pub.id ? { ...p, [key]: !pub[key] } : p)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onDrop = async (targetIndex) => {
    if (dragIndex === null || dragIndex === targetIndex) {
      setDragIndex(null);
      return;
    }
    const reordered = [...pubs];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    setPubs(reordered);
    setDragIndex(null);
    try {
      await api.post("/admin/publishers/reorder", { ids: reordered.map((p) => p.id) });
      toast.success("Sıralama güncellendi");
    } catch (e) {
      toast.error(formatApiError(e));
      fetchPubs();
    }
  };

  const inputCls =
    "w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-[#64748B] outline-none focus:border-cyan-400/60 transition-colors";

  return (
    <div data-testid="admin-publishers-page">
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-display font-700 text-xl text-white">Yayıncı Yönetimi</h1>
        <button onClick={openCreate} data-testid="publisher-add-button" className="btn-glow inline-flex items-center gap-2 text-xs font-semibold text-white px-5 py-2.5 rounded-full">
          <Plus size={15} /> Yeni Yayıncı
        </button>
      </div>
      <p className="text-sm text-[#64748B] mb-8">Sürükleyerek sıralamayı değiştirebilirsiniz. Sıralama ana sayfaya doğrudan yansır.</p>

      {loading ? (
        <p className="text-sm text-[#64748B]">Yükleniyor...</p>
      ) : pubs.length === 0 ? (
        <div data-testid="publishers-admin-empty" className="rounded-2xl border border-dashed border-white/10 bg-[#0A0E17]/60 py-16 text-center">
          <p className="text-[#A0ABC0] text-sm mb-5">Henüz yayıncı eklenmemiş.</p>
          <button onClick={openCreate} data-testid="publisher-add-empty-button" className="btn-glow inline-flex items-center gap-2 text-xs font-semibold text-white px-5 py-2.5 rounded-full">
            <Plus size={15} /> İlk Yayıncıyı Ekle
          </button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="publishers-admin-list">
          {pubs.map((pub, index) => (
            <div
              key={pub.id}
              draggable
              onDragStart={() => setDragIndex(index)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(index)}
              data-testid={`publisher-admin-row-${pub.id}`}
              className={`flex items-center gap-4 rounded-2xl border border-white/[0.06] bg-[#0A0E17] p-4 transition-colors ${
                dragIndex === index ? "opacity-50 border-cyan-400/40" : ""
              }`}
            >
              <GripVertical size={18} className="text-[#64748B] cursor-grab shrink-0" data-testid={`publisher-drag-${pub.id}`} />
              <span className="text-xs text-[#64748B] w-6 text-center shrink-0">{index + 1}</span>
              {pub.profile_image ? (
                <img src={imageUrl(pub.profile_image)} alt={pub.name} className="w-11 h-11 rounded-xl object-cover shrink-0" />
              ) : (
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0047FF] to-[#00F0FF] flex items-center justify-center shrink-0">
                  <span className="font-display text-sm text-white">{pub.name?.charAt(0)?.toUpperCase()}</span>
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-white truncate">{pub.name}</p>
                <p className="text-xs text-cyan-300 truncate">{pub.tiktok_username} {pub.category ? `· ${pub.category}` : ""}</p>
              </div>
              <div className="hidden md:flex flex-col items-center gap-1 shrink-0">
                <span className="text-[10px] text-[#64748B]">Aktif</span>
                <button
                  onClick={() => toggle(pub, "is_active")}
                  data-testid={`publisher-active-toggle-${pub.id}`}
                  className={`w-10 h-5.5 h-[22px] rounded-full transition-colors relative ${pub.is_active ? "bg-gradient-to-r from-[#0047FF] to-[#00F0FF]" : "bg-white/10"}`}
                  aria-label="Aktif/Pasif"
                >
                  <span className={`absolute top-[3px] w-4 h-4 rounded-full bg-white transition-all ${pub.is_active ? "left-[22px]" : "left-[3px]"}`} />
                </button>
              </div>
              <div className="hidden md:flex flex-col items-center gap-1 shrink-0">
                <span className="text-[10px] text-[#64748B]">Ana Sayfa</span>
                <button
                  onClick={() => toggle(pub, "show_on_homepage")}
                  data-testid={`publisher-homepage-toggle-${pub.id}`}
                  className={`w-10 h-[22px] rounded-full transition-colors relative ${pub.show_on_homepage ? "bg-gradient-to-r from-[#0047FF] to-[#00F0FF]" : "bg-white/10"}`}
                  aria-label="Ana sayfada göster"
                >
                  <span className={`absolute top-[3px] w-4 h-4 rounded-full bg-white transition-all ${pub.show_on_homepage ? "left-[22px]" : "left-[3px]"}`} />
                </button>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => setPreview(pub)} data-testid={`publisher-preview-${pub.id}`} aria-label="Önizle"
                  className="w-8 h-8 rounded-lg border border-white/10 flex items-center justify-center text-[#A0ABC0] hover:text-cyan-300 hover:border-cyan-400/40 transition-colors">
                  <Eye size={14} />
                </button>
                <button onClick={() => openEdit(pub)} data-testid={`publisher-edit-${pub.id}`} aria-label="Düzenle"
                  className="w-8 h-8 rounded-lg border border-white/10 flex items-center justify-center text-[#A0ABC0] hover:text-cyan-300 hover:border-cyan-400/40 transition-colors">
                  <Pencil size={14} />
                </button>
                <button onClick={() => setDeleteId(pub.id)} data-testid={`publisher-delete-${pub.id}`} aria-label="Sil"
                  className="w-8 h-8 rounded-lg border border-white/10 flex items-center justify-center text-[#A0ABC0] hover:text-red-300 hover:border-red-400/40 transition-colors">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0A0E17] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="publisher-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-base">{editing ? "Yayıncıyı Düzenle" : "Yeni Yayıncı Ekle"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="publisher-form">
            <div>
              <label className="block text-xs font-semibold text-[#A0ABC0] mb-2">Profil Görseli</label>
              <div className="flex items-center gap-4">
                {form.profile_image ? (
                  <img src={imageUrl(form.profile_image)} alt="Profil" className="w-16 h-16 rounded-xl object-cover" data-testid="publisher-image-preview" />
                ) : (
                  <div className="w-16 h-16 rounded-xl border border-dashed border-white/15 flex items-center justify-center text-[#64748B]">
                    <Upload size={18} />
                  </div>
                )}
                <div>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files[0])} data-testid="publisher-image-input" />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    data-testid="publisher-image-upload-button"
                    className="text-xs font-semibold text-cyan-300 border border-cyan-400/30 rounded-full px-4 py-2 hover:bg-cyan-400/10 transition-colors disabled:opacity-50"
                  >
                    {uploading ? "Yükleniyor..." : form.profile_image ? "Görseli Değiştir" : "Görsel Yükle"}
                  </button>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-[#A0ABC0] mb-2">Yayıncı Adı *</label>
                <input data-testid="publisher-name-input" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Yayıncı adı" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#A0ABC0] mb-2">TikTok Kullanıcı Adı *</label>
                <input data-testid="publisher-username-input" className={inputCls} value={form.tiktok_username} onChange={(e) => setForm({ ...form, tiktok_username: e.target.value })} placeholder="@kullaniciadi" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#A0ABC0] mb-2">İçerik Kategorisi</label>
                <input data-testid="publisher-category-input" className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Örn: Oyun, Sohbet" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#A0ABC0] mb-2">TikTok Profil Bağlantısı</label>
                <input data-testid="publisher-url-input" className={inputCls} value={form.tiktok_url} onChange={(e) => setForm({ ...form, tiktok_url: e.target.value })} placeholder="https://www.tiktok.com/@..." />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#A0ABC0] mb-2">Kısa Açıklama</label>
              <textarea data-testid="publisher-description-input" rows={3} className={`${inputCls} resize-none`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Yayıncı hakkında kısa açıklama" />
            </div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input type="checkbox" data-testid="publisher-active-checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4 accent-cyan-400" />
                <span className="text-xs text-[#A0ABC0]">Aktif</span>
              </label>
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input type="checkbox" data-testid="publisher-homepage-checkbox" checked={form.show_on_homepage} onChange={(e) => setForm({ ...form, show_on_homepage: e.target.checked })} className="w-4 h-4 accent-cyan-400" />
                <span className="text-xs text-[#A0ABC0]">Ana sayfada göster</span>
              </label>
            </div>
            <button type="submit" disabled={saving} data-testid="publisher-save-button" className="btn-glow w-full text-sm font-semibold text-white py-3.5 rounded-xl disabled:opacity-50">
              {saving ? "Kaydediliyor..." : editing ? "Değişiklikleri Kaydet" : "Yayıncıyı Ekle"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-[#050505] border-white/10 max-w-sm" data-testid="publisher-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-base text-white">Kart Önizleme</DialogTitle>
          </DialogHeader>
          {preview && <PublisherCard pub={preview} testid="publisher-preview-card" />}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-[#0A0E17] border-white/10 text-white" data-testid="publisher-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Yayıncıyı Sil</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A0ABC0]">Bu yayıncıyı silmek istediğinize emin misiniz?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="publisher-delete-cancel" className="bg-transparent border-white/10 text-white hover:bg-white/5">Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="publisher-delete-confirm" onClick={remove} className="bg-red-500 hover:bg-red-600 text-white">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
